import { useEffect, useRef, useState } from 'react';
import { Copy } from 'lucide-react';
import { useTranslation } from '../../../lib/i18n';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { Button, Input, Typography } from '../../symphony/ui';
import { SETTINGS_PAGE } from './settings-shared';
import styles from './InviteLinkRow.module.css';

const COPIED_LABEL_MS = 2000;

/**
 * The caller's invite link under its own heading, beside a Copy button (Figma 943-18172: a 42-tall
 * field and button on one row, 16 apart). The field is READ-ONLY — the link is issued by the server
 * and attributed to this account, so it is something to take, never something to edit.
 *
 * Copy is the PRIMARY action of this modal, hence the standard black skin: with the share-sheet CTA
 * gone from the footer, taking the link is the only thing the screen asks the user to do.
 *
 * The button is disabled until the link is known, and that is a correctness rule rather than
 * polish: an empty or half-known link copies something that credits nobody, and the sharer only
 * finds out by never being paid. iOS states the same rule on its share CTA.
 */
export function InviteLinkRow({
  shareLink,
  loading,
  size = 'large',
}: {
  shareLink: string;
  loading: boolean;
  /**
   * The height of the whole row — field and button together. The 943 frame draws it at 42; this modal's
   * own frame draws every part of the row at 36 (its `Symphony Input` is 62 tall including a 20 label,
   * with Copy bottom-aligned at y=26). 36 is safe HERE despite the ship-large rule because neither phone
   * renders this field at all, so there is no field height to stay in parity with.
   */
  size?: 'large' | 'medium';
}) {
  const { t } = useTranslation();
  const { logModalActionBi } = useGenericBi();
  const [copied, setCopied] = useState(false);
  // A second click restarts the window rather than letting the first timer clear the new label.
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => (timer.current ? clearTimeout(timer.current) : undefined), []);

  const copy = async () => {
    if (!shareLink) return;
    logModalActionBi({ ...SETTINGS_PAGE, modal_name: 'share_app', action: 'copy_invite_link' });
    try {
      await navigator.clipboard.writeText(shareLink);
    } catch {
      // A denied clipboard is the browser's decision, not an app error, and the link is on screen
      // and selectable either way — so say nothing rather than raise an error for a blocked copy.
      return;
    }
    setCopied(true);
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(false), COPIED_LABEL_MS);
  };

  return (
    <div className={styles.block}>
      <Typography variant="h4" component="h3">
        {t('settings.shareApp.link.heading')}
      </Typography>
      <div className={styles.row}>
        <Input readOnly size={size} value={shareLink} aria-label={t('settings.shareApp.link.label')} className={styles.field} />
        <Button size={size} startIcon={<Copy size={16} aria-hidden />} disabled={!shareLink || loading} onClick={() => void copy()}>
          {copied ? t('common.copied') : t('settings.shareApp.link.copy')}
        </Button>
      </div>
    </div>
  );
}
